/**
 * Geometry formulas for 6th-grade MGSR standards.
 * All computations use exact Rational arithmetic.
 */
import { Rational, rat } from "./rational";

/** Area of a rectangle: l * w */
export function areaRectangle(length: Rational, width: Rational): Rational {
  return length.mul(width);
}

/** Area of a triangle: (1/2) * b * h */
export function areaTriangle(base: Rational, height: Rational): Rational {
  return base.mul(height).div(rat(2));
}

/** Area of a parallelogram: b * h */
export function areaParallelogram(base: Rational, height: Rational): Rational {
  return base.mul(height);
}

/** Area of a trapezoid: (1/2) * (b1 + b2) * h */
export function areaTrapezoid(base1: Rational, base2: Rational, height: Rational): Rational {
  return base1.add(base2).mul(height).div(rat(2));
}

/** Volume of a rectangular prism: l * w * h */
export function volumeRectPrism(length: Rational, width: Rational, height: Rational): Rational {
  return length.mul(width).mul(height);
}

/** Surface area of a rectangular prism: 2(lw + lh + wh) */
export function surfaceAreaRectPrism(l: Rational, w: Rational, h: Rational): Rational {
  const lw = l.mul(w);
  const lh = l.mul(h);
  const wh = w.mul(h);
  return lw.add(lh).add(wh).mul(rat(2));
}

/**
 * Surface area of a right triangular prism given triangle sides,
 * triangle height (for area), and prism length.
 */
export function surfaceAreaTriPrism(
  triBase: Rational,
  triHeight: Rational,
  side1: Rational,
  side2: Rational,
  side3: Rational,
  prismLength: Rational,
): Rational {
  const triArea = areaTriangle(triBase, triHeight);
  const lateralArea = side1.add(side2).add(side3).mul(prismLength);
  return triArea.mul(rat(2)).add(lateralArea);
}

/**
 * Surface area of a rectangular pyramid: base area + (1/2) * perimeter * slant height.
 * For 6th grade, we compute via net decomposition: base + 4 triangle faces.
 */
export function surfaceAreaRectPyramid(
  baseL: Rational,
  baseW: Rational,
  slantL: Rational,
  slantW: Rational,
): Rational {
  const baseArea = baseL.mul(baseW);
  const frontBack = baseL.mul(slantW).div(rat(2)).mul(rat(2));
  const sides = baseW.mul(slantL).div(rat(2)).mul(rat(2));
  return baseArea.add(frontBack).add(sides);
}

/** Complement of an angle (90 - angle). Returns null if angle >= 90. */
export function complementaryAngle(angle: Rational): Rational | null {
  const comp = rat(90).sub(angle);
  if (comp.isNegative() || comp.isZero()) return null;
  return comp;
}

/** Supplement of an angle (180 - angle). Returns null if angle >= 180. */
export function supplementaryAngle(angle: Rational): Rational | null {
  const supp = rat(180).sub(angle);
  if (supp.isNegative() || supp.isZero()) return null;
  return supp;
}

/** Distance between two points on the same axis (horizontal or vertical). */
export function coordinateDistance(a: Rational, b: Rational): Rational {
  return a.sub(b).abs();
}
